// Small pieces shared by the ticket lists (dashboard queue, archive): the
// tone of a status pill, the channel mark, the client's identity and mood,
// French dates and numbers. Layout lives in TicketTableDashboard.css (.tl-*).
import React from 'react';
import { Globe, Mail } from 'lucide-react';
import { SiGmail, SiInstagram, SiMessenger, SiMicrosoftoutlook, SiWhatsapp } from 'react-icons/si';
import { getStatusClass } from '../constants/ticketStatuses';
import { formatShortDate, formatShortDateTime } from '../utils/dates';

export const TL_NS = 'ticketListsUi';

// Status family (constants/ticketStatuses) → badge tone of the kit:
// waiting = warning, to act on = danger, AI = accent, done = success.
const TONE_BY_CLASS = {
  'status-new': 'info',
  'status-waiting': 'warning',
  'status-processing': 'info',
  'status-pending': 'danger',
  'status-automated': 'accent',
  'status-resolved': 'success',
};

export const statusTone = (status) => TONE_BY_CLASS[getStatusClass(status)];

// Third-party marks keep their own colour (kit § Icônes); our own channels
// (web widget, custom inbox) use lucide.
const CHANNEL_MARKS = {
  Gmail: (props) => <SiGmail color="#EA4335" {...props} />,
  Outlook: (props) => <SiMicrosoftoutlook color="#0078D4" {...props} />,
  WhatsApp: (props) => <SiWhatsapp color="#25D366" {...props} />,
  Instagram: (props) => <SiInstagram color="#E4405F" {...props} />,
  Messenger: (props) => <SiMessenger color="#0084FF" {...props} />,
  Website: (props) => <Globe strokeWidth={1.8} {...props} />,
  Custom: (props) => <Mail strokeWidth={1.8} {...props} />,
};

export const ChannelMark = ({ name }) => {
  const Mark = CHANNEL_MARKS[name];
  if (!Mark) return null;
  return <Mark className="tl-channel" aria-hidden="true" focusable="false" />;
};

// Dates: the shared short format (utils/dates — parseUtc, Paris time).
// « 28 oct., 09:40 » — the full value of a date cell (its title).
export const formatParisDateTime = (value) => formatShortDateTime(value, undefined, { empty: '' });

// « 28 oct. » this year, « 28 oct. 2025 » before — what a date cell shows.
export const formatParisDay = (value) => formatShortDate(value, undefined, { empty: '' });

// French grouping with a plain no-break space (the narrow one renders as no
// gap in Inter at table sizes): « 1 178 ».
export const fmtFr = (value) => Number(value).toLocaleString('fr-FR').replace(/\u202f/g, '\u00a0');

export const initialsOf = (text) => {
  const words = String(text || '').replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
  if (!words.length) return '?';
  return words.slice(0, 2).map((word) => word[0].toUpperCase()).join('');
};

// "m.dupont@orange.fr" → "M Dupont": the display name is derived from the
// email itself (we store no separate customer name), never invented.
export const prettyNameFromEmail = (email) => {
  if (!email) return null;
  const local = String(email).split('@')[0];
  const words = local.split(/[._-]+/).filter(Boolean);
  if (!words.length) return String(email);
  return words
    .map((word) => (word.length > 1 ? word[0].toUpperCase() + word.slice(1) : word.toUpperCase()))
    .join(' ');
};

// Who the ticket is from, the same way in every list: the company the
// backend found, else a name read from the e-mail. In FA mode company_name
// carries the shop/brand mapping, not a customer identity — never shown
// there. « Unknown » is the backend's « no company found » placeholder.
export const clientIdentity = (ticket, { hasFaAccount = false } = {}) => {
  const email = ticket?.customer_email || '';
  const rawCompany = hasFaAccount ? '' : (ticket?.company_name || '');
  const company = rawCompany === 'Unknown' ? '' : rawCompany;
  return { name: company || prettyNameFromEmail(email), email };
};

// The mood (AI sentiment, 0–100) opens the client's second line: a tone dot
// and the number in the secondary ink, in a fixed slot so the channel marks
// after it line up from row to row. `toneOf` is the list's own scale (the
// archive reads scores differently from the open queue).
export const MoodInline = ({ score, toneOf, title, emptyTitle }) => {
  const value = score === null || score === undefined || score === '' ? NaN : Number(score);
  if (!Number.isFinite(value)) {
    return <span className="tl-mood tl-mood--none" title={emptyTitle}>—</span>;
  }
  const tone = toneOf(value) || 'neutral';
  return (
    <span className={`tl-mood tl-mood--${tone}`} title={title}>
      <span className="tl-mood__dot" aria-hidden="true" />
      {Math.round(value)}
    </span>
  );
};
